// RelayUnderAttackAlert — fires a small banner + screen-edge ping when
// a viewer-owned relay becomes contested. Distinct from
// CriticalFlipBanner (which is for high-severity state changes like
// link_cut / relay_isolated): this fires earlier in the loss chain —
// the moment an enemy squad steps onto your relay's capture ring.
//
// Per-relay throttle (RELAY_REMIND_MS) so a sustained contest doesn't
// spam the channel. The banner reuses the CriticalFlipBanner-style
// stack (small top-of-screen pill) so the visual language is
// consistent with the existing alerts.

import type {
  EntityId,
  MatchConnection,
  MatchState,
  PlayerId,
  Unsubscribe,
} from '@signalclash/shared';

const ROOT_ID = 'relay-under-attack-alerts';
const STYLE_ID = 'relay-under-attack-styles';
const RELAY_REMIND_MS = 10_000;
const BANNER_DURATION_MS = 3500;
// Floor on the absolute capture-progress at which a "contested" alert
// fires. Below this, the bar has barely moved and the alert would be
// noise. The simulation reports `contested` as a boolean but we only
// fire when progress is clearly dropping toward enemy territory.
const PROGRESS_FLOOR = 0.05;

interface ActiveBanner {
  el: HTMLDivElement;
  timer: ReturnType<typeof setTimeout>;
}

export class RelayUnderAttackAlert {
  private root: HTMLDivElement | null = null;
  private viewerId: PlayerId;
  private prev: MatchState | null = null;
  private unsub: Unsubscribe | null = null;
  private lastFiredAt = new Map<EntityId, number>();
  private active: ActiveBanner[] = [];

  constructor(viewerId: PlayerId) {
    this.viewerId = viewerId;
    injectStyles();
    this.root = ensureRoot();
  }

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
    this.lastFiredAt.clear();
  }

  attach(connection: MatchConnection): void {
    this.detach();
    this.unsub = connection.onSnapshot((snap) => this.onSnapshot(snap));
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;
    this.prev = null;
  }

  clear(): void {
    for (const b of this.active) {
      clearTimeout(b.timer);
      b.el.remove();
    }
    this.active.length = 0;
    this.lastFiredAt.clear();
  }

  hide(): void {
    if (this.root !== null) this.root.style.display = 'none';
  }

  show(): void {
    if (this.root !== null) this.root.style.display = 'flex';
  }

  dispose(): void {
    this.detach();
    this.clear();
    if (this.root !== null) this.root.remove();
    this.root = null;
  }

  private onSnapshot(snap: MatchState): void {
    if (this.prev === null) {
      this.prev = snap;
      return;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    for (const [idStr, node] of Object.entries(snap.controlNodes)) {
      if (node.ownerId !== this.viewerId) continue;
      const id = idStr as unknown as EntityId;
      const before = this.prev.controlNodes[id];
      if (before === undefined) continue;
      // Trigger: was-not-contested -> now-contested for our relay, OR
      // captureProgress moving in the enemy's favour by at least PROGRESS_FLOOR
      // since the last frame.
      const becameContested = !before.contested && node.contested;
      // captureProgress is signed; we treat any nonzero progress as a
      // direction. The enemy is "the other player" — they push progress
      // toward sign(-viewerId). For viewerId=0 the enemy is sign==-1;
      // for viewerId=1 the enemy is sign==+1.
      const enemySign = this.viewerId === 0 ? -1 : 1;
      const enemyMoved =
        Math.sign(node.captureProgress) === enemySign &&
        Math.abs(node.captureProgress) > Math.abs(before.captureProgress) + PROGRESS_FLOOR;
      if (!becameContested && !enemyMoved) continue;
      const lastAt = this.lastFiredAt.get(id) ?? -Infinity;
      if (now - lastAt < RELAY_REMIND_MS) continue;
      this.lastFiredAt.set(id, now);
      this.show();
      this.pushBanner();
    }
    this.prev = snap;
  }

  private pushBanner(): void {
    if (this.root === null) return;
    const el = document.createElement('div');
    el.className = 'relay-under-attack-banner';
    el.textContent = 'RELAY UNDER ATTACK';
    this.root.prepend(el);
    const timer = setTimeout(() => {
      el.classList.add('relay-under-attack-banner--out');
      setTimeout(() => {
        el.remove();
        const idx = this.active.findIndex((b) => b.el === el);
        if (idx !== -1) this.active.splice(idx, 1);
      }, 220);
    }, BANNER_DURATION_MS);
    this.active.push({ el, timer });
    // Cap to 2 visible at once.
    while (this.active.length > 2) {
      const oldest = this.active.shift();
      if (oldest !== undefined) {
        clearTimeout(oldest.timer);
        oldest.el.remove();
      }
    }
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: absolute;
  top: 110px;
  left: 50%;
  transform: translateX(-50%);
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  z-index: 25;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.relay-under-attack-banner {
  padding: 7px 14px;
  font-size: 12px;
  letter-spacing: 0.08em;
  border-radius: 4px;
  color: #ff8c64;
  border: 1px solid currentColor;
  background: rgba(28, 14, 10, 0.92);
  box-shadow: 0 0 12px currentColor;
  animation: relay-under-attack-pulse 360ms ease-out;
}
.relay-under-attack-banner--out {
  animation: relay-under-attack-out 220ms ease-in forwards;
}
@keyframes relay-under-attack-pulse {
  0%   { transform: translateY(-6px) scale(0.95); opacity: 0; }
  60%  { transform: translateY(0) scale(1.04); opacity: 1; }
  100% { transform: translateY(0) scale(1.0); opacity: 1; }
}
@keyframes relay-under-attack-out {
  0%   { opacity: 1; }
  100% { opacity: 0; transform: translateY(-4px); }
}
@media (max-width: 820px), (pointer: coarse) {
  #${ROOT_ID} { top: 120px; }
}
`;
  document.head.appendChild(style);
}
